/// <amd-module name="Analytics/Boards/BurndownQueries/CompletedWorkItemsQuery" />
import { FieldFilter, WorkItemFieldDescriptor } from "Analytics/Boards/BoardsQueries/DataContracts";
import { AggregationMode, ModefulValueSetting, TeamScope } from "Analytics/Boards/BoardsQueries/DataContracts";
import { WitFieldUtilities } from "Analytics/Boards/BoardsQueries/WitFieldUtilities";
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as PublicProjectsQueryHelper from "Analytics/Common/Utilities/PublicProjectsQueryHelper";
import { QueryUtilities } from "Analytics/Common/Utilities/QueryUtilities";
import { latestODataVersion } from "Analytics/Common/Utilities/QueryUtilities";
import { IVssPageContext } from "VSS/Platform/Context";
import { uniqueSort } from "VSS/Platform/Util/Array";
import { BurndownQueryBase } from "./BurndownQueryBase";

export interface ODataWorkItemSnapshot {
    WorkItemId: string;
}

export class CompletedWorkItemIdsQuery extends BurndownQueryBase<string[]> {
    private static generateQueryOptions(
        context: IVssPageContext,
        teamScopes: TeamScope[],
        filters: FieldFilter[],
        workItemTypes: string[],
        workItemFieldDescriptors: WorkItemFieldDescriptor[],
        aggregation: ModefulValueSetting<AggregationMode, string>,
        showResolvedItemsAsCompleted = false
    ): ODataQueryOptions {
        const projects: string[] = uniqueSort(teamScopes.map(scope => scope.projectId));

        const projectsClause = QueryUtilities.isInArray("ProjectSK", projects, false /* useQuotes */);

        const teams = uniqueSort(teamScopes.map(scope => scope.teamId));
        const teamsClause = QueryUtilities.isInArray("t/TeamSK", teams, false /* useQuotes */);

        const workItemTypesClause = QueryUtilities.isInArray("WorkItemType", workItemTypes, true /* useQuotes */);

        let $apply =
            `filter(` +
            `(${projectsClause})` +
            ` and Teams/any(t:${teamsClause})` +
            ` and (StateCategory eq 'Completed'`;

        if(showResolvedItemsAsCompleted){
            $apply += 
            ` or StateCategory eq 'Resolved'` +
            ` or State eq 'Resolved'`
        }

        $apply +=
        `) and (${workItemTypesClause})`;

        if (aggregation.identifier === AggregationMode.Sum) {
            const analyticsEffortFieldName = WitFieldUtilities.getFieldODataPropertyName(aggregation.settings);
            $apply += ` and ${analyticsEffortFieldName} ne null`;
        }

        const filtersClause = WitFieldUtilities.getFiltersClause(filters, workItemFieldDescriptors);
        if (filtersClause) {
            $apply += ` and (${filtersClause})`;
        }

        $apply += ")";

        return PublicProjectsQueryHelper.forceProjectScoping(context, projects, {
            entityType: "WorkItems",
            oDataVersion: latestODataVersion,
            $apply: $apply,
            $select: "WorkItemId"
        });
    }
    constructor(
        pageContext: IVssPageContext,
        teamScopes: TeamScope[],
        filters: FieldFilter[],
        workItemTypes: string[],
        workItemFieldDescriptors: WorkItemFieldDescriptor[],
        aggregation: ModefulValueSetting<AggregationMode, string>,
        showResolvedItemsAsCompleted = false,
        commandName?: string
    ) {
        super(
            CompletedWorkItemIdsQuery.generateQueryOptions(pageContext, teamScopes, filters, workItemTypes, workItemFieldDescriptors, aggregation, showResolvedItemsAsCompleted),
            commandName
        );
    }

    public getQueryName(): string {
        return "CompletedWorkItemIdsQuery";
    }

    protected interpretQueryResults(data: { value: ODataWorkItemSnapshot[] }): string[] {
        return data.value.map(value => value.WorkItemId);
    }
}
